/*
 * @description 移动端 Popup 弹出层组件
 *  - API 参考 @tencent/tea-component-mobile 的 Popup
 *  - 内容由调用方通过 children 传入
 *  - 支持 top / bottom / left / right / center 五个方向
 */
import { CSSProperties, ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './index.css';

export type PopupPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';

export type PopupCloseSource = 'mask' | 'call';

export interface PopupProps {
  /** 是否可见（受控） */
  visible: boolean;
  /** 弹出位置，默认 bottom */
  position?: PopupPosition;
  /** 弹出层内容（由用户传入） */
  children?: ReactNode;
  /** 是否展示遮罩，默认 true */
  mask?: boolean;
  /** 点击遮罩是否关闭，默认 true */
  maskClosable?: boolean;
  /** 关闭时是否销毁内容，默认 true */
  destroyOnClose?: boolean;
  /** 浮层层级，默认 1000 */
  zIndex?: number;
  /** 偏移距离 */
  offset?: number | string;
  /** 动画时长（ms），默认 300 */
  duration?: number;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: CSSProperties;
  /** 遮罩自定义样式 */
  maskStyle?: CSSProperties;
  /** 显示状态变化回调 */
  onVisibleChange?: (visible: boolean) => void;
  /** 关闭时回调 */
  onClose?: (ctx: { source: PopupCloseSource; event?: React.MouseEvent }) => void;
  /** 挂载节点，默认挂载到 document.body */
  getContainer?: () => HTMLElement;
}

const toCssSize = (v?: number | string) => (typeof v === 'number' ? `${v}px` : v);

const Popup = memo((props: PopupProps) => {
  const {
    visible,
    position = 'bottom',
    children,
    mask = true,
    maskClosable = true,
    destroyOnClose = true,
    zIndex = 99999,
    offset,
    duration = 300,
    className,
    style,
    maskStyle,
    onVisibleChange,
    onClose,
    getContainer,
  } = props;

  // mounted: 控制内容是否挂载在 DOM；active: 控制 CSS 进入态（用于触发过渡动画）
  const [mounted, setMounted] = useState<boolean>(visible);
  const [active, setActive] = useState<boolean>(false);
  const enterTimerRef = useRef<number | null>(null);
  const exitTimerRef = useRef<number | null>(null);

  const clearEnter = () => {
    if (enterTimerRef.current !== null) {
      window.clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
  };
  const clearExit = () => {
    if (exitTimerRef.current !== null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (visible) {
      clearExit();
      setMounted(true);
      // 下一帧再激活，确保过渡生效
      enterTimerRef.current = window.setTimeout(() => {
        setActive(true);
      }, 16);
    } else {
      clearEnter();
      setActive(false);
      // 等动画结束再卸载
      exitTimerRef.current = window.setTimeout(() => {
        if (destroyOnClose) {
          setMounted(false);
        }
      }, duration);
    }
    return () => {
      clearEnter();
      clearExit();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, duration, destroyOnClose]);

  const handleMaskClick = useCallback(
    (event: React.MouseEvent) => {
      if (!maskClosable) return;
      onClose?.({ source: 'mask', event });
      onVisibleChange?.(false);
    },
    [maskClosable, onClose, onVisibleChange],
  );

  // 计算内容容器样式（位置 + 偏移 + 过渡）
  const contentStyle = useMemo<CSSProperties>(() => {
    const base: CSSProperties = {
      transitionDuration: `${duration}ms`,
      ...style,
    };
    const off = toCssSize(offset);
    if (off !== undefined) {
      switch (position) {
        case 'top':
          base.top = off;
          break;
        case 'bottom':
          base.bottom = off;
          break;
        case 'left':
          base.left = off;
          break;
        case 'right':
          base.right = off;
          break;
        case 'center':
        default:
          break;
      }
    }
    return base;
  }, [duration, offset, position, style]);

  const maskComputedStyle = useMemo<CSSProperties>(
    () => ({
      transitionDuration: `${duration}ms`,
      ...maskStyle,
    }),
    [duration, maskStyle],
  );

  // 不挂载且不显示，直接 return null
  if (!mounted && !visible) {
    return null;
  }

  const wrapperClass = [
    'webcast-popup',
    `webcast-popup--${position}`,
    active ? 'webcast-popup--active' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const node = (
    <div className="webcast-popup-root" style={{ zIndex }}>
      {mask && (
        <div
          className={`webcast-popup__mask ${active ? 'webcast-popup__mask--active' : ''}`}
          style={maskComputedStyle}
          onClick={handleMaskClick}
        />
      )}
      <div className={wrapperClass} style={contentStyle} role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );

  const container = getContainer?.() || (typeof document !== 'undefined' ? document.body : null);
  if (!container) return null;
  return createPortal(node, container);
});

Popup.displayName = 'Popup';

export default Popup;
